import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Target } from "lucide-react";
import { MissionSize } from "@/types/todo";
import { MISSION_SIZE_REQUIREMENTS } from "@/utils/missionHelpers";
interface MissionCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateMission: (name: string, size: MissionSize) => void;
}

const MissionCreationModal = ({ 
  isOpen, 
  onClose, 
  onCreateMission 
}: MissionCreationModalProps) => {
  const [name, setName] = useState('');
  const [selectedSize, setSelectedSize] = useState<MissionSize>(MissionSize.S);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    onCreateMission(name.trim(), selectedSize);
    
    // Reset form
    setName('');
    setSelectedSize(MissionSize.S);
    onClose();
  };

  const handleClose = () => {
    // Reset form on close
    setName('');
    setSelectedSize(MissionSize.S);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2">
            <Target className="text-primary" size={20} />
            Create New Mission
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Mission Name */}
          <div className="space-y-2">
            <Label htmlFor="mission-name">Mission Name</Label>
            <Input
              id="mission-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter mission name..."
              required
            />
          </div>

          {/* Size Selection */}
          <div className="space-y-3">
            <Label>Mission Size</Label>
            <ToggleGroup
              type="single"
              value={selectedSize}
              onValueChange={(value) => {
                if (value) setSelectedSize(value as MissionSize);
              }}
              variant="outline"
              className="grid grid-cols-5 gap-2"
            >
              {Object.values(MissionSize).map((size) => (
                <ToggleGroupItem
                  key={size}
                  value={size}
                  className="flex flex-col items-center gap-1 p-3 h-auto"
                >
                  <Badge variant="secondary" className="text-xs">
                    {size}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {MISSION_SIZE_REQUIREMENTS[size]}
                  </span>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()} className="flex-1">
              Create Mission
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MissionCreationModal;